import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    Button,
    Paper,
    Typography,
    FormControl,
    InputLabel,
    Select
} from "@mui/material";
import { Localized } from "@fluent/react";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AppRequiredMessage } from "../../../app/AppRequiredMessage";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { postWithAuth } from "../../../http";
import { StructureTreeSelect } from "../../structure/StructureTreeSelect";
import { AppId } from "../../../app/appTypes";
import { CancelButton } from "../../../app/components/CancelButton";
import { SaveButton } from "../../../app/components/SaveButton";
import produce from "immer";
import { AreaUnit, CleanlinessClassGmp, createRoomGroup, CreateRoomGroupPayload, NewRoomPayload, Room, RoomGroup, VolumeUnit } from "../roomsApi";
import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Popover } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { selectAllStructureNodes } from "../../device/structureSlice";

export interface CreateRoomGroupDialogProps {
    onSuccess: (group: RoomGroup) => void;
    onCancel: () => void;
}

const CLEANLINESS_OPTIONS: CleanlinessClassGmp[] = ["A", "B", "C", "D"];
const AREA_UNITS: AreaUnit[] = ["SQUARE_METER", "SQUARE_FOOT"];
const VOLUME_UNITS: VolumeUnit[] = ["CUBIC_METER", "CUBIC_FOOT", "LITER"];

const emptyRoom = (): Room => ({
    id: 0,
    companyId: 0,
    code: "",
    name: "",
    cleanlinessClass: "D",
    roomArea: 0,
    roomAreaUnit: "SQUARE_METER",
    cubicCapacity: 0,
    cubicCapacityUnit: "CUBIC_METER",
    structureId: null,
    roomGroupId: null,
    createdBy: "",
    createdByFullName: "",
    createDate: new Date(),
});

export const CreateRoomGroupDialog = ({ onSuccess, onCancel }: CreateRoomGroupDialogProps) => {
    const dispatch = useAppDispatch();

    const rootDeviceId = useAppSelector(state => selectAllStructureNodes(state).find(s => s.code === 'INF')?.id)

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [structureId, setStructureId] = useState<AppId | undefined>();

    const [rooms, setRooms] = useState<Room[]>([emptyRoom()]);

    const [validationErrors, setValidationErrors] = useState(false);
    const [saving, setSaving] = useState(false);

    // handlers – grupa
    const handleNameChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setName(e.target.value),
        []
    );
    const handleDescriptionChange = useMemo(
        () => (e: ChangeEvent<HTMLInputElement>) => setDescription(e.target.value),
        []
    );
    const handleStructureChanged = useCallback((id: AppId | undefined) => setStructureId(id), []);


    const handleCommit = useCallback((index: number, patch: Partial<Room>) => {
        setRooms(prev => {
            const next = [...prev];
            next[index] = { ...next[index], ...patch };
            return next;
        });
    }, []);

    const handleRowRemove = useCallback((index: number) => {
        setRooms(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    }, []);

    const handleRowAdd = useCallback(() => setRooms(prev => [...prev, emptyRoom()]), []);
    // walidacja
    const nameError = validationErrors && name.trim() === "";


    const handleSaveClick = async () => {
        setValidationErrors(true);

        const groupOk = name.trim() !== "";
        const hasAtLeastOneRoom = rooms.length > 0;

        const everyRoomOk =
            rooms.length > 0 &&
            rooms.every((r) => {
                const okCode = r.code.trim() !== "";
                const okName = r.name.trim() !== "";
                const area = r.roomArea === 0 ? 0 : Number(r.roomArea);
                const cap = r.cubicCapacity === 0 ? 0 : Number(r.cubicCapacity);
                const okArea = !Number.isNaN(area) && area >= 0;
                const okCap = !Number.isNaN(cap) && cap >= 0;
                return okCode && okName && okArea && okCap;
            });

        if (!groupOk || !hasAtLeastOneRoom || !everyRoomOk) return;

        setSaving(true);
        try {
            const payload: CreateRoomGroupPayload = {
                name: name.trim(),
                description: description.trim() || null,
                structureId: structureId ?? "",
                companyId: 0 as any,
                rooms: rooms.map<NewRoomPayload>((r) => ({
                    code: r.code.trim(),
                    name: r.name.trim(),
                    cleanlinessClass: r.cleanlinessClass,
                    roomArea: r.roomArea === 0 ? 0 : Number(r.roomArea),
                    roomAreaUnit: r.roomAreaUnit,
                    cubicCapacity: r.cubicCapacity === 0 ? 0 : Number(r.cubicCapacity),
                    cubicCapacityUnit: r.cubicCapacityUnit,
                    structureId: r.structureId ?? null
                })),
                createdBy: "",
                createdByFullName: "",
                createDate: new Date()
            };

            const result = await dispatch(createRoomGroup(payload));
            const created = unwrapResult(result);

            if (createRoomGroup.fulfilled.match(result)) {
                dispatch(showSuccess("saved"));
                onSuccess(created);
            } else {
                let errorMsg = "Błąd zapisu";
                const p: any = (result as any).payload;
                if (p?.kind === "http" && p?.problem?.title) errorMsg = p.problem.title;
                dispatch(showError(errorMsg));
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog maxWidth="lg" fullWidth open onClose={onCancel}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "grey.100",
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                }}
            >
                <MeetingRoomIcon fontSize="small" color="success" />
                <Localized id="rg-create-title">Nowa grupa pomieszczeń</Localized>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <StructureTreeSelect defaultStructureId={structureId} onStructureSelected={handleStructureChanged} rootId={rootDeviceId} />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            error={nameError}
                            helperText={nameError && <AppRequiredMessage />}
                            value={name}
                            inputProps={{ maxLength: 200 }}
                            onChange={handleNameChange}
                            label={<Localized id="rg-name">Nazwa grupy</Localized>}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            value={description}
                            inputProps={{ maxLength: 2000 }}
                            onChange={handleDescriptionChange}
                            label={<Localized id="rg-description">Opis (opcjonalnie)</Localized>}
                            multiline
                            minRows={2}
                        />
                    </Grid>

                    {/* Lista pokoi */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 1,
                                    bgcolor: "grey.100",
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 1,
                                    border: (t) => `1px solid ${t.palette.divider}`,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <MeetingRoomIcon fontSize="small" color="action" />
                                    <Typography variant="subtitle2">
                                        <Localized id="rg-rooms-title">Pomieszczenia</Localized>
                                    </Typography>
                                </Box>

                                <Button startIcon={<AddIcon />} onClick={handleRowAdd} color="success" variant="contained" size="small">
                                    <Localized id="add">Dodaj</Localized>
                                </Button>
                            </Box>

                            <Grid container spacing={1} sx={{ p: 1.5 }}>
                                {rooms.map((room, idx) => (
                                    <RoomRowItem
                                        key={room.id || `tmp-${idx}`}   // jeśli możesz, dodaj tempId przy tworzeniu
                                        room={room}
                                        index={idx}
                                        validationOn={validationErrors}
                                        onCommit={handleCommit}
                                        onRemove={handleRowRemove}
                                        disableRemove={rooms.length === 1}
                                    />
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ bgcolor: "background.paper", borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};

const RoomRowItem = React.memo(function RoomRowItem({
    room,
    index,
    validationOn,
    onCommit,
    onRemove,
    disableRemove
}: {
    room: Room;
    index: number;
    validationOn: boolean;
    onCommit: (index: number, patch: Partial<Room>) => void;
    onRemove: (index: number) => void;
    disableRemove: boolean;
}) {
    // lokalny stan tylko tego wiersza
    const [local, setLocal] = useState(room);
    const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
    const openHelp = (e: React.MouseEvent<HTMLElement>) => setHelpAnchor(e.currentTarget);
    const closeHelp = () => setHelpAnchor(null);

    // jeśli rodzic podmieni room z zewnątrz (np. reset), zsynchronizuj
    useEffect(() => { setLocal(room); }, [room.id]);

    const codeError = validationOn && local.code.trim() === "";
    const nameError = validationOn && local.name.trim() === "";
    const areaError = validationOn && !(local.roomArea >= 0);
    const volError = validationOn && !(local.cubicCapacity >= 0);
    const rowInvalid = codeError || nameError || areaError || volError;

    // helper commit
    const commit = useCallback((patch: Partial<Room>) => onCommit(index, patch), [index, onCommit]);

    return (
        <Grid item xs={24}>
            <Grid container columns={{ xs: 24, md: 24 }} spacing={1} alignItems="center"
                sx={{ border: (t) => `1px solid ${rowInvalid ? t.palette.error.light : t.palette.divider}`, borderRadius: 1, p: 1 }}>
                <Grid item xs={1} sx={{ display: "flex", justifyContent: "center" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>

                        <Box
                            sx={{
                                mt: 0.5,
                                px: 1,
                                py: 0.5,
                                bgcolor: "grey.100",
                                borderRadius: 1,
                                minWidth: 32,
                                textAlign: "center",
                                fontWeight: 600,
                            }}
                        >
                            {index + 1}
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        required size="small" margin="dense" fullWidth
                        error={codeError}
                        value={local.code}
                        onChange={(e) => setLocal((s) => ({ ...s, code: e.target.value }))}
                        onBlur={() => commit({ code: local.code })}
                        label="Kod"
                    />
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        required size="small" margin="dense" fullWidth
                        error={nameError}
                        value={local.name}
                        onChange={(e) => setLocal((s) => ({ ...s, name: e.target.value }))}
                        onBlur={() => commit({ name: local.name })}
                        label="Nazwa"
                    />
                </Grid>

                <Grid item xs={2}>
                    <FormControl fullWidth size="small" margin="dense">
                        <InputLabel id={"cls"} sx={{ pointerEvents: "auto" }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                <Localized id="room-cleanliness">Klasa</Localized>
                                <Tooltip title={<Localized id="gmp-help">Informacja o klasach</Localized> as any}>
                                    <IconButton
                                        size="small"
                                        onClick={openHelp}
                                        onMouseDown={(e) => e.preventDefault()} // nie zabieraj fokusu
                                        tabIndex={-1}
                                        sx={{ p: 0.25 }}
                                        aria-label="Informacja o klasach GMP"
                                    >
                                        <InfoOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </InputLabel>

                        <Select
                            labelId={"labelId"}
                            label="Klasa" // potrzebne dla 'notch'
                            value={local.cleanlinessClass}
                            onChange={(e) => {
                                const v = e.target.value as CleanlinessClassGmp;
                                setLocal((s) => ({ ...s, cleanlinessClass: v }));
                                onCommit(index, { cleanlinessClass: v });
                            }}
                            // <- DZIAŁA: wybór A/B/C/D z klawiatury bez otwierania menu
                            onKeyDownCapture={(e) => {
                                const k = e.key.toUpperCase();
                                if (k === "A" || k === "B" || k === "C" || k === "D") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const v = k as CleanlinessClassGmp;
                                    setLocal((s) => ({ ...s, cleanlinessClass: v }));
                                    onCommit(index, { cleanlinessClass: v });
                                    // zablokuj otwarcie menu, które MUI próbuje wywołać po keydown

                                }
                            }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
                        >
                            {CLEANLINESS_OPTIONS.map((c) => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Popover
                        open={Boolean(helpAnchor)}
                        anchorEl={helpAnchor}
                        onClose={closeHelp}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                        <Box sx={{ p: 1.5, maxWidth: 360 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                <Localized id="gmp-title">Klasy czystości (EU GMP Annex 1)</Localized>
                            </Typography>
                            <Box sx={{ fontSize: 13, lineHeight: 1.5 }}>
                                <strong>A</strong> — ISO 4.8 / ISO 5 — aseptyczne operacje (napełnianie, zamykanie).<br />
                                <strong>B</strong> — ISO 5–6 — tło dla klasy A; przygotowanie operatora/sprzętu.<br />
                                <strong>C</strong> — ISO 7 — przygotowanie roztworów, napełnianie przed sterylizacją.<br />
                                <strong>D</strong> — ISO 8 — mniej krytyczne etapy: przygotowanie materiałów, mycie itd.
                            </Box>
                        </Box>
                    </Popover>
                </Grid>


                <Grid item xs={3}>
                    <TextField
                        size="small" margin="dense" fullWidth type="number" inputProps={{ min: 0, step: "0.01" }}
                        error={areaError}
                        value={local.roomArea}
                        onChange={(e) => setLocal((s) => ({ ...s, roomArea: e.target.value === "" ? 0 : Number(e.target.value) }))}
                        onBlur={() => commit({ roomArea: local.roomArea })}
                        label="Powierzchnia"
                    />
                </Grid>

                <Grid item xs={2}>
                    <TextField
                        select size="small" margin="dense" fullWidth label="Jedn. pow."
                        value={local.roomAreaUnit}
                        onChange={(e) => { const v = e.target.value as AreaUnit; setLocal((s) => ({ ...s, roomAreaUnit: v })); commit({ roomAreaUnit: v }); }}
                    >
                        {["SQUARE_METER", "SQUARE_FOOT"].map((u) => (
                            <MenuItem key={u} value={u}>{u === "SQUARE_METER" ? "m²" : "ft²"}</MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        size="small" margin="dense" fullWidth type="number" inputProps={{ min: 0, step: "0.001" }}
                        error={volError}
                        value={local.cubicCapacity}
                        onChange={(e) => setLocal((s) => ({ ...s, cubicCapacity: e.target.value === "" ? 0 : Number(e.target.value) }))}
                        onBlur={() => commit({ cubicCapacity: local.cubicCapacity })}
                        label="Kubatura"
                    />
                </Grid>

                <Grid item xs={2}>
                    <TextField
                        select size="small" margin="dense" fullWidth label="Jedn. kub."
                        value={local.cubicCapacityUnit}
                        onChange={(e) => { const v = e.target.value as VolumeUnit; setLocal((s) => ({ ...s, cubicCapacityUnit: v })); commit({ cubicCapacityUnit: v }); }}
                    >
                        {["CUBIC_METER", "CUBIC_FOOT", "LITER"].map((u) => (
                            <MenuItem key={u} value={u}>{u === "CUBIC_METER" ? "m³" : u === "CUBIC_FOOT" ? "ft³" : "L"}</MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Tooltip title="Usuń">
                        <span>
                            <IconButton color="error" onClick={() => onRemove(index)} disabled={disableRemove} size="small">
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Grid>
            </Grid>
        </Grid>
    );
});
